import { DateTime } from "luxon";
import { FC, MouseEvent, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ActionsWrap, Td } from "@/components/Table";
import { MetaBusinessAccountResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as PlusIcon } from "@/assets/icons/add-bordered.svg";
import Form from "./Form";
import Text from "@/components/Text";
import ChannelsAttachmentForm from "./ChannelsAttachmentForm";

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
`;

interface Props {
  account: MetaBusinessAccountResource;
}

const TableRow: FC<Props> = ({ account }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPagesModal, setShowPagesModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const handleRowClick = (id: number) => {
    navigate(`/general/meta-business-accounts/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this account?`,
        "All assigned pages will no longer be associated with this business account"
      );

      if (!confirm) return;

      await api.delete(`/v1/meta-business-accounts/${account.id}`);

      await queryClient.invalidateQueries(["meta_business_accounts"]);

      alerts.success("Account was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <Tr onClick={() => handleRowClick(account.id)}>
      <Td>
        <Text as="span" $type="smallBold">
          {account.name}
        </Text>
      </Td>
      <Td>{DateTime.fromISO(account.createdAt).toFormat("yyyy/LL/dd")}</Td>
      <Td>{account.channels_count ?? 0}</Td>
      <Td>{account.notes ?? "-"}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setShowPagesModal(true);
              }}
            >
              <span>
                <PlusIcon />
              </span>
              Add page
            </ActionsDropdownButton>
            <ActionsDropdownButton
              as="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setShowEditModal(true);
              }}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
        <AnimatePresence>
          {showEditModal && (
            <Form
              editableAccount={account}
              onClose={() => setShowEditModal(false)}
            />
          )}
          {showPagesModal && (
            <ChannelsAttachmentForm
              accountId={account.id}
              onClose={() => setShowPagesModal(false)}
            />
          )}
        </AnimatePresence>
      </Td>
    </Tr>
  );
};

export default TableRow;
